import React, { useState, useEffect } from "react";
import NewCommonModalCenter from "../../../../components/common/NewCommonModalCenter";
import InputField from "../../../../components/common/InputField";
import MultiSelectInput from "../../../../components/common/MultiSelectInput";
import {
  fetchHrDepartments,
  fetchHrDesignations,
  createHrLeaveType,
  updateHrLeaveType,
} from "../../../../apis/hrApi";
import { useRolesData } from "../../../../apis/staffApis";
import toast from "react-hot-toast";

type AddEditLeaveTypeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  leaveTypeToEdit?: any | null;
  isSidebar?: boolean;
};

const GENDER_OPTIONS = [
  { value: "MALE", text: "Male" },
  { value: "FEMALE", text: "Female" },
  { value: "OTHER", text: "Other" },
];

const MARITAL_STATUS_OPTIONS = [
  { value: "SINGLE", text: "Single" },
  { value: "MARRIED", text: "Married" },
  { value: "WIDOWED", text: "Widowed" },
  { value: "DIVORCED", text: "Divorced" },
];

export default function AddEditLeaveTypeModal({
  isOpen,
  onClose,
  onSave,
  leaveTypeToEdit,
  isSidebar = false,
}: AddEditLeaveTypeModalProps) {
  const [activeTab, setActiveTab] = useState<"General" | "Entitlement" | "Applicability">("General");
  const [isLoading, setIsLoading] = useState(false);

  // Form States
  const [leaveType, setLeaveType] = useState("");
  const [noOfLeaves, setNoOfLeaves] = useState(1);
  const [monthlyLimit, setMonthlyLimit] = useState(1);
  const [color, setColor] = useState("#E53E3E");
  const [isPaidLeave, setIsPaidLeave] = useState(true);

  const [effectiveAfter, setEffectiveAfter] = useState(0);
  const [effectiveAfterUnit, setEffectiveAfterUnit] = useState("MONTHS");
  const [unusedLeavesPolicy, setUnusedLeavesPolicy] = useState("CARRY_FORWARD");
  const [allowInNoticePeriod, setAllowInNoticePeriod] = useState(false);
  const [allowInProbation, setAllowInProbation] = useState(false);
  const [priorPermissionRequired, setPriorPermissionRequired] = useState(false);

  const [departmentIds, setDepartmentIds] = useState<string[]>([]);
  const [designationIds, setDesignationIds] = useState<string[]>([]);
  const [userRoleIds, setUserRoleIds] = useState<string[]>([]);
  const [genderFilters, setGenderFilters] = useState<string[]>([]);
  const [maritalStatusFilters, setMaritalStatusFilters] = useState<string[]>([]);

  // Selection Options fetched from API
  const [departmentOptions, setDepartmentOptions] = useState<{ value: string; text: string; id: string }[]>([]);
  const [designationOptions, setDesignationOptions] = useState<{ value: string; text: string; id: string }[]>([]);
  const [roleOptions, setRoleOptions] = useState<{ value: string; text: string; id: string }[]>([]);

  const { data: rolesData } = useRolesData();

  useEffect(() => {
    if (rolesData?.roles) {
      setRoleOptions(
        rolesData.roles.map((r: any) => ({
          id: r.id,
          value: r.id,
          text: r.roleName,
        }))
      );
    }
  }, [rolesData]);

  useEffect(() => {
    if (!isOpen) return;

    // Reset fields or load existing data if editing
    if (leaveTypeToEdit) {
      setLeaveType(leaveTypeToEdit.leaveType || "");
      setNoOfLeaves(leaveTypeToEdit.noOfLeaves ?? 1);
      setMonthlyLimit(leaveTypeToEdit.monthlyLimit ?? 1);
      setColor(leaveTypeToEdit.color || "#E53E3E");
      setIsPaidLeave(leaveTypeToEdit.isPaidLeave ?? true);

      setEffectiveAfter(leaveTypeToEdit.effectiveAfter ?? 0);
      setEffectiveAfterUnit(leaveTypeToEdit.effectiveAfterUnit || "MONTHS");
      setUnusedLeavesPolicy(leaveTypeToEdit.unusedLeavesPolicy || "CARRY_FORWARD");
      setAllowInNoticePeriod(leaveTypeToEdit.allowInNoticePeriod ?? false);
      setAllowInProbation(leaveTypeToEdit.allowInProbation ?? false);
      setPriorPermissionRequired(leaveTypeToEdit.priorPermissionRequired ?? false);

      setDepartmentIds(leaveTypeToEdit.departmentIds || []);
      setDesignationIds(leaveTypeToEdit.designationIds || []);
      setUserRoleIds(leaveTypeToEdit.userRoleIds || []);
      setGenderFilters(leaveTypeToEdit.genderFilters || []);
      setMaritalStatusFilters(leaveTypeToEdit.maritalStatusFilters || []);
    } else {
      setLeaveType("");
      setNoOfLeaves(1);
      setMonthlyLimit(1);
      setColor("#E53E3E");
      setIsPaidLeave(true);

      setEffectiveAfter(0);
      setEffectiveAfterUnit("MONTHS");
      setUnusedLeavesPolicy("CARRY_FORWARD");
      setAllowInNoticePeriod(false);
      setAllowInProbation(false);
      setPriorPermissionRequired(false);

      setDepartmentIds([]);
      setDesignationIds([]);
      setUserRoleIds([]);
      setGenderFilters([]);
      setMaritalStatusFilters([]);
    }

    setActiveTab("General");

    // Fetch Applicability options
    const loadApplicabilityData = async () => {
      try {
        const deptRes = await fetchHrDepartments(1, 100);
        if (deptRes.success && deptRes.data?.departments) {
          setDepartmentOptions(
            deptRes.data.departments.map((d: any) => ({
              id: d.id,
              value: d.id,
              text: d.departmentName,
            }))
          );
        }

        const desRes = await fetchHrDesignations(1, 100);
        if (desRes.success && desRes.data?.designations) {
          setDesignationOptions(
            desRes.data.designations.map((d: any) => ({
              id: d.id,
              value: d.id,
              text: d.designationName,
            }))
          );
        }

        // Roles are loaded via React Query hook useRolesData
      } catch (err) {
        console.error(err);
      }
    };

    loadApplicabilityData();
  }, [isOpen, leaveTypeToEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leaveType.trim()) {
      toast.error("Please enter Leave Type name.");
      return;
    }

    setIsLoading(true);

    const payload = {
      leaveType,
      noOfLeaves,
      monthlyLimit,
      color,
      isPaidLeave,
      effectiveAfter,
      effectiveAfterUnit,
      unusedLeavesPolicy,
      allowInNoticePeriod,
      allowInProbation,
      priorPermissionRequired,
      departmentIds,
      designationIds,
      userRoleIds,
      genderFilters,
      maritalStatusFilters,
    };

    try {
      let res;
      if (leaveTypeToEdit) {
        res = await updateHrLeaveType(leaveTypeToEdit.id || leaveTypeToEdit._id, payload);
      } else {
        res = await createHrLeaveType(payload);
      }

      if (res.success) {
        toast.success(
          leaveTypeToEdit
            ? "Leave Type updated successfully"
            : "Leave Type created successfully"
        );
        onSave();
        onClose();
      } else {
        toast.error(res.message || "Failed to save Leave Type");
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while saving.");
    } finally {
      setIsLoading(false);
    }
  };

  const formContent = (
    <form onSubmit={handleSubmit} className={`bg-white p-6 ${isSidebar ? "max-h-[calc(100vh-160px)] overflow-y-auto" : "rounded-b-3xl max-h-[90vh] overflow-y-auto"}`}>
      {/* Horizontal Navigation Tabs */}
      <div className="flex bg-slate-100 rounded-xl p-1 mb-6 border border-slate-200">
        {(["General", "Entitlement", "Applicability"] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`flex-1 text-center py-2 text-xs font-semibold rounded-lg transition cursor-pointer border-none bg-transparent ${
              activeTab === tab
                ? "bg-white text-slate-800 shadow-sm font-bold"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab contents */}
      {activeTab === "General" && (
        <div className="space-y-4 animate-in fade-in duration-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="Leave Type *"
              placeholder="Enter leave type (Ex. Casual, Sick)"
              value={leaveType}
              onChange={(e) => setLeaveType(e.target.value)}
              required
            />
            <InputField
              label="No of Leaves *"
              type="number"
              value={noOfLeaves.toString()}
              onChange={(e) => setNoOfLeaves(Number(e.target.value))}
              required
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="Monthly Limit *"
              type="number"
              value={monthlyLimit.toString()}
              onChange={(e) => setMonthlyLimit(Number(e.target.value))}
              required
            />
            <div className="space-y-1">
              <label className="text-[#1E293B] text-xs font-semibold block">Color</label>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-full h-10 border border-gray-200 rounded-xl cursor-pointer p-0 overflow-hidden"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer pt-2">
            <input
              type="checkbox"
              checked={isPaidLeave}
              onChange={(e) => setIsPaidLeave(e.target.checked)}
              className="w-4 h-4 accent-[#FBD037]"
            />
            <span>Leave Paid</span>
          </label>
        </div>
      )}

      {activeTab === "Entitlement" && (
        <div className="space-y-4 animate-in fade-in duration-200">
          <div className="space-y-1">
            <label className="text-[#1E293B] text-xs font-semibold">Effective After</label>
            <div className="flex gap-2">
              <input
                type="number"
                value={effectiveAfter}
                onChange={(e) => setEffectiveAfter(Number(e.target.value))}
                className="w-full text-xs px-3 py-[10px] rounded-xl border border-gray-200 focus:outline-none"
              />
              <span className="text-xs self-center text-slate-500 font-semibold min-w-max">of joining</span>
              <select
                value={effectiveAfterUnit}
                onChange={(e) => setEffectiveAfterUnit(e.target.value)}
                className="text-xs px-3 py-[10px] rounded-xl border border-gray-200 bg-white focus:outline-none"
              >
                <option value="DAYS">Days</option>
                <option value="MONTHS">Months</option>
                <option value="YEARS">Years</option>
              </select>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[#1E293B] text-xs font-semibold">Unused Leaves *</label>
            <select
              value={unusedLeavesPolicy}
              onChange={(e) => setUnusedLeavesPolicy(e.target.value)}
              className="w-full text-xs px-3 py-[10px] rounded-xl border border-gray-200 bg-white focus:outline-none"
            >
              <option value="CARRY_FORWARD">Carry Forward</option>
              <option value="EXPIRE">Expire</option>
              <option value="PAID">Paid</option>
            </select>
          </div>

          <div className="flex flex-wrap items-center gap-6 pt-2">
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={allowInNoticePeriod}
                onChange={(e) => setAllowInNoticePeriod(e.target.checked)}
                className="w-4 h-4 accent-[#FBD037]"
              />
              <span>In Notice Period</span>
            </label>
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={allowInProbation}
                onChange={(e) => setAllowInProbation(e.target.checked)}
                className="w-4 h-4 accent-[#FBD037]"
              />
              <span>In Probation</span>
            </label>
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={priorPermissionRequired}
                onChange={(e) => setPriorPermissionRequired(e.target.checked)}
                className="w-4 h-4 accent-[#FBD037]"
              />
              <span>Prior Permission</span>
            </label>
          </div>
        </div>
      )}

      {activeTab === "Applicability" && (
        <div className="space-y-4 animate-in fade-in duration-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MultiSelectInput
              name="departments"
              label="Departments"
              placeholder="Select Option..."
              selectOptions={departmentOptions}
              value={departmentIds}
              setValue={setDepartmentIds}
            />
            <MultiSelectInput
              name="designations"
              label="Designations"
              placeholder="Select Option..."
              selectOptions={designationOptions}
              value={designationIds}
              setValue={setDesignationIds}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MultiSelectInput
              name="userRoles"
              label="User Roles"
              placeholder="Select User Roles..."
              selectOptions={roleOptions}
              value={userRoleIds}
              setValue={setUserRoleIds}
            />
            <MultiSelectInput
              name="genders"
              label="Gender"
              placeholder="Select Gender..."
              selectOptions={GENDER_OPTIONS}
              value={genderFilters}
              setValue={setGenderFilters}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MultiSelectInput
              name="maritalStatuses"
              label="Marital Status"
              placeholder="Select Marital Status..."
              selectOptions={MARITAL_STATUS_OPTIONS}
              value={maritalStatusFilters}
              setValue={setMaritalStatusFilters}
            />
          </div>
        </div>
      )}

      {/* Form actions footer */}
      <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-slate-100">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-200 hover:bg-gray-50 text-xs font-semibold rounded-xl text-slate-600 transition cursor-pointer bg-white"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 bg-[#FBD037] text-[#212121] hover:bg-[#e2bd30] text-xs font-bold rounded-xl transition cursor-pointer border-none flex items-center justify-center min-w-max shadow-sm hover:shadow-md duration-150 hover:-translate-y-0.5 active:translate-y-0"
        >
          {isLoading ? (
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 text-[#212121]"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8z"
              />
            </svg>
          ) : null}
          <span>{leaveTypeToEdit ? "Update Leave Type" : "Create Leave Type"}</span>
        </button>
      </div>
    </form>
  );

  if (isSidebar) {
    return formContent;
  }

  return (
    <NewCommonModalCenter
      open={isOpen}
      onClose={onClose}
      title={leaveTypeToEdit ? "Edit Leave Type" : "New Leave Type"}
      hideFooter={true}
      classWidth="w-11/12 max-w-xl"
    >
      {formContent}
    </NewCommonModalCenter>
  );
}
